import React, { Component, Fragment } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { Button, Grid, Modal } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import axios, { CancelTokenSource } from 'axios';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import CheckBox from 'components/final-form/CheckBox';
import { getAccount2faData, setAccount2faData } from 'services/accountServices';
import PmVerificationInput from 'components/final-form/PmVerificationInput';
import styled from 'styled-components';
import { errorUtils } from 'utils/errorUtils';
import { FormApi } from 'final-form';

const TwoFactorAuthModalContainer = styled(FormModalContentContainer)`
  .form-grid {
    margin-bottom: 1rem;

    .label {
      display: inline-block;
      margin: 0 0.5rem 0.5rem;
    }

    .verification-input__character {
      text-align: center;
    }

    .error {
      margin-left: 0.5rem;
      color: darkred;
    }
  }

  .success {
    color: darkgreen;
    display: inline-block;
    margin-left: 0.5rem;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void
}

interface State {
  initialized: boolean,
  verificationCodeKey: number,
  error?: string,
  showUpdateSuccess: boolean,
  cancelTokenSource: CancelTokenSource
}

interface TwoStepAuthValues {
  use2fa: boolean;
  qrcodeUrl: string;
  verificationCode: string;
}

class TwoStepAuthModal extends Component<Props, State> {

  initialValues: TwoStepAuthValues;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      initialized: false,
      verificationCodeKey: 1,
      error: undefined,
      showUpdateSuccess: false,
      cancelTokenSource
    };

    this.initialValues = {
      use2fa: false,
      qrcodeUrl: "",
      verificationCode: ""
    };

    this.get2faData();
  }

  get2faData = async () => {

    const {currentUser} = this.props;
    const {cancelTokenSource} = this.state;

    if (currentUser) {
      const user2faData = await getAccount2faData();

      this.initialValues = {
        use2fa: user2faData.use2fa,
        qrcodeUrl: user2faData.qrcodeUrl,
        verificationCode: ""
      };

      this.setState({
        initialized: true
      });
    }
  };

  handleSubmit = async (values: Partial<TwoStepAuthValues>, form: FormApi) => {

    try {
      await setAccount2faData(values);
    } catch (ex) {
      let error = "error.general";
      const {response} = ex;
      if (!!response.data && !!response.data.violations) {

        const {violations} = response.data;

        const violation = violations[0];

        if ([errorUtils.invalidVerificationCode].includes(violation.errorCode)) {
          error = `config.2fa.modal.error.${violation.errorCode}`;
        }
      }

      this.setState({
        verificationCodeKey: this.state.verificationCodeKey + 1,
        error: error
      });

      form.change("verificationCode", undefined);

      setTimeout(() => {
        (document.getElementsByClassName("verification-input")[0]! as any).focus();
      }, 50);

      return;
    }

    this.setState({
      showUpdateSuccess: true
    });
    setTimeout(() => {
      this.props.onCloseModal();
    }, 1500);

  };

  render(): React.ReactNode {

    const {initialized} = this.state;
    const {modalOpen, onCloseModal, options, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "400px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("config.2fa.modal.title")}</Modal.Header>
          <TwoFactorAuthModalContainer>
            {
              initialized
                  ?
                  <FinalForm
                      onSubmit={this.handleSubmit}
                      subscription={{pristine: true, submitting: true, values: true}}
                      render={this.renderForm}
                      initialValues={this.initialValues}
                  />
                  :
                  <></>
            }
          </TwoFactorAuthModalContainer>
        </Modal>
    );
  }

  renderForm = ({form, submitting, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCloseModal} = this.props;

    const values = form.getState().values;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid className="form-grid">
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign={"middle"}>
                    <Field
                        name="use2fa"
                        component={CheckBox}
                        label={getMessage('config.2fa.modal.use2fa')}
                        toggle
                    />
                  </Grid.Column>
                </Grid.Row>
                {
                  values.use2fa &&
                  <Fragment>
                    <Grid.Row>
                      <Grid.Column width={10} verticalAlign={"middle"}>
                        <img src={values.qrcodeUrl}/>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={14} verticalAlign={"middle"}>
                        <label htmlFor="verificationCode" className="label">
                          {getMessage("config.2fa.modal.verificationCode.label")}
                        </label>
                        <Field
                            key={this.state.verificationCodeKey}
                            name='verificationCode'
                            component={PmVerificationInput}
                            autoFocus={true}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {
                      this.state.error &&
                      <Grid.Row>
                        <Grid.Column width={14} verticalAlign={"middle"}>
                          <div className="error">
                            {getMessage(this.state.error)}
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                    }
                  </Fragment>
                }
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCloseModal}>{getMessage("button.cancel")}</Button>
              {
                this.state.showUpdateSuccess &&
                <div className="success">{getMessage("config.2fa.modal.success")}</div>
              }
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  }
}

export default withMessages(withAuthContext(TwoStepAuthModal));
